/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { Paths, appStoreUrl, googlePlayUrl } from "const"
import { ActionButton, Card, HelpBand } from "components/help-page"

const faqUrl = "https://medaica.com/pages/heart-exam-faq"

const Holding = (): ReactElement => {
  return (
    <div className="max-w-4xl mx-auto pb-8">
      <div className="text-center py-10">
        <h1 className="text-4xl font-extrabold tracking-tight">Medaica At-Home Heart Exam</h1>
        <p className="text-lg text-gray-500 mt-3">
          Everything you need for your Medaica At-Home telehealth appointment
        </p>
      </div>

      <Card
        title="Schedule or Join Your Exam"
        lead={
          "Ready for your Medaica At-Home Heart Exam? Book your guided appointment or join your scheduled exam " +
          "from here."
        }
      >
        <div className="grid grid-cols-1 gap-5">
          <ActionButton
            to={Paths.booking}
            variant="primary"
            title="See Your Guided Exam Details"
            subtitle="Choose a time that works for you or join an existing appointment"
          />
        </div>
      </Card>

      <Card
        title="For Exams on Laptops or Desktop Computers"
        lead={
          "Please test your equipment prior to your scheduled appointment to ensure a smooth exam experience. " +
          "Use Google Chrome on your computer."
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <ActionButton
            to={Paths.liveExamCompatibilityTest}
            variant="secondary"
            title="Test Equipment & Browser"
            subtitle="Run a quick compatibility test"
          />
          <ActionButton to={Paths.liveExam} variant="primary" title="Join Guided Exam" subtitle="via access code" />
        </div>
      </Card>

      <Card
        title="Download the Medaica App On Your Mobile Phone"
        lead="If you are using a mobile phone or tablet for your exam, please download the Medaica Patient app."
      >
        <div className="flex flex-wrap items-center gap-5 ml-5">
          <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
            <img
              className="h-12 w-auto"
              src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
              alt="Download on the App Store"
            />
          </a>
          <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer">
            <img
              className="h-12 w-auto"
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
            />
          </a>
        </div>
      </Card>

      <Card
        title="Frequently Asked Questions"
        lead="Have questions about the exam, your device, or what to expect? Visit our comprehensive FAQ page."
      >
        <div className="grid grid-cols-1 gap-5">
          <ActionButton
            href={faqUrl}
            variant="secondary"
            title="View All FAQs"
            subtitle="Find answers to common questions"
          />
        </div>
      </Card>

      <HelpBand heading="Need Additional Help?" lead="Our fast and friendly support team is here to help you." />
    </div>
  )
}

export default Holding
